/**
 * useResultsViewParam — keeps the results tab and the URL in sync (Phase 3).
 *
 * Ownership: the URL owns the *static* views. A tab change pushes a history
 * entry (so Back/Forward walks the tabs); Back/Forward writes the tab back.
 * Per-ETF views are not addressable AND they must not touch the URL either:
 * picking an ETF leaves the last static claim in place, so the history does not
 * grow an entry per selection and Back keeps walking the portfolio views.
 */
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  DEFAULT_RESULT_VIEW, VIEW_PARAM, isStaticResultView, resolveResultView,
} from "@/utils/resultsView";

export function useResultsViewParam({ activeTab, setActiveTab, enabled, availableTabs }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const param = searchParams.get(VIEW_PARAM);
  const lastApplied = useRef(null);

  // URL → state (first render, deep link, Back/Forward).
  useEffect(() => {
    if (!enabled) return;
    if (param === lastApplied.current) return;
    lastApplied.current = param;
    if (param === null) return;                 // no claim in the URL
    const next = resolveResultView(param, availableTabs);
    if (next !== activeTab) setActiveTab(next);
    if (next !== param) {
      // Invalid / stale value: normalise it without adding history noise.
      const params = new URLSearchParams(searchParams);
      params.set(VIEW_PARAM, next);
      setSearchParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [param, enabled, availableTabs]);

  // No results to address yet: the URL must not keep a stale claim.
  useEffect(() => {
    if (enabled || param === null) return;
    const params = new URLSearchParams(searchParams);
    params.delete(VIEW_PARAM);
    lastApplied.current = null;
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, param]);

  // state → URL. Only the static views are serialised; a per-ETF view leaves
  // the URL exactly as it is (no history entry, no removal).
  const hydrated = useRef(false);
  useEffect(() => {
    if (!enabled) return;
    if (!hydrated.current) {
      // First pass: a deep link wins, `activeTab` has not been hydrated yet.
      hydrated.current = true;
      if (param !== null) return;
    }
    if (!isStaticResultView(activeTab)) return;
    const params = new URLSearchParams(searchParams);
    if (params.get(VIEW_PARAM) === activeTab) return;
    params.set(VIEW_PARAM, activeTab);
    lastApplied.current = activeTab;
    setSearchParams(params, { replace: activeTab === DEFAULT_RESULT_VIEW && param === null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, enabled]);
}

export default useResultsViewParam;
